import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Camera,
  Check,
  KeyRound,
  LogOut,
  Mail,
  Monitor,
  Phone,
  ShieldCheck,
  Store,
  Trash2,
} from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';

import { ApiClientError } from '@terra/api-client';
import type { users } from '@terra/contracts';
import { Badge, Button, Input, Spinner, toast } from '@terra/ui';

import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';

const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

const errMsg = (e: unknown, fallback: string) =>
  e instanceof ApiClientError ? e.message : fallback;

/**
 * Uploads an image the way every other upload goes: a signature from the API,
 * the bytes straight to Cloudinary, then the result registered as a public
 * FileObject owned by this account. Returns the file's id.
 */
async function uploadImage(file: File, folder: string): Promise<string> {
  if (!file.type.startsWith('image/')) throw new Error('Choose an image file');
  if (file.size > MAX_IMAGE_BYTES) throw new Error('Image must be under 2 MB');
  const sig = await api.files.getUploadSignature({ folder });
  const fd = new FormData();
  fd.append('file', file);
  fd.append('api_key', sig.apiKey);
  fd.append('timestamp', String(sig.timestamp));
  fd.append('signature', sig.signature);
  fd.append('folder', sig.folder);
  const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloudName}/image/upload`, {
    method: 'POST',
    body: fd,
  });
  const out = (await res.json()) as { secure_url?: string; error?: { message: string } };
  if (!res.ok || !out.secure_url) throw new Error(out.error?.message ?? 'Upload failed');
  const registered = await api.files.registerSelf({
    name: file.name,
    url: out.secure_url,
    contentType: file.type,
    sizeBytes: file.size,
    visibility: 'PUBLIC',
  });
  return registered.id;
}

/** Keeps the signed-in user in the shell current without a full re-bootstrap. */
function patchSessionUser(
  patch: Partial<NonNullable<ReturnType<typeof useAuthStore.getState>['user']>>,
) {
  const user = useAuthStore.getState().user;
  if (user) useAuthStore.setState({ user: { ...user, ...patch } });
}

function Panel({
  title,
  description,
  right,
  children,
  className = '',
}: {
  title: string;
  description: string;
  right?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm ${className}`}
    >
      <div className="border-border/60 flex items-start justify-between gap-3 border-b px-5 py-4">
        <div className="min-w-0">
          <h2 className="text-ink text-[15px] font-bold">{title}</h2>
          <p className="text-ink-subtle mt-0.5 text-[12.5px] font-medium">{description}</p>
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

/**
 * The signed-in seller's own account: their store's logo, who they are, how
 * they sign in, and which devices are signed in. The same controls the admin
 * panel's "My account" gives staff, plus the logo shoppers see on the store.
 *
 * Every sign-in change re-asks for the current password, and phone or email
 * are only saved after a code sent to the *new* address is entered.
 */
export function AccountPage() {
  const user = useAuthStore((s) => s.user);

  return (
    <div className="space-y-6 pb-8">
      <div>
        <h1 className="text-ink text-xl font-bold tracking-tight">My account</h1>
        <p className="text-ink-subtle mt-1 text-[13px] font-medium">
          Your store logo, name, picture, sign-in details and the devices you are signed in on.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
        <div className="space-y-6 self-start">
          <StoreLogoCard />
          <ProfileCard user={user} />
        </div>
        <div className="space-y-6">
          <PasswordCard />
          <ContactCard
            channel="SMS"
            current={user?.phone ?? ''}
            verified={user?.phoneVerified ?? false}
          />
          <ContactCard
            channel="EMAIL"
            current={user?.email ?? ''}
            verified={user?.emailVerified ?? false}
          />
          <SessionsCard />
        </div>
      </div>
    </div>
  );
}

// ── Store logo ─────────────────────────────────────────────────────────────

function StoreLogoCard() {
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const store = useQuery({ queryKey: ['vendor-store'], queryFn: () => api.vendors.getMyStore() });

  const save = useMutation({
    mutationFn: (logoFileId: string | null) => api.vendors.updateMyStore({ logoFileId }),
    onSuccess: async (_r, logoFileId) => {
      await qc.invalidateQueries({ queryKey: ['vendor-store'] });
      toast.success(logoFileId ? 'Store logo updated' : 'Store logo removed');
    },
    onError: (e) => toast.error(errMsg(e, 'Could not update the store logo')),
  });

  const onPick = async (file: File) => {
    setUploading(true);
    try {
      await save.mutateAsync(await uploadImage(file, 'vendor-logos'));
    } catch (e) {
      if (!(e instanceof ApiClientError)) {
        toast.error(e instanceof Error ? e.message : 'Upload failed');
      }
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const logoUrl = store.data?.logoUrl ?? null;
  const name = store.data?.storeName ?? 'Store';
  const busy = uploading || save.isPending;

  return (
    <Panel
      title="Store logo"
      description="Shown on your store page on the website and in the app."
      right={<Store className="text-ink-subtle h-4 w-4" />}
    >
      <div className="flex items-center gap-4 px-5 py-5">
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          disabled={busy || store.isLoading}
          className="group relative h-20 w-20 shrink-0 overflow-hidden rounded-full bg-amber-500 text-2xl font-bold text-white shadow-sm ring-4 ring-white"
          aria-label="Change store logo"
        >
          {logoUrl ? (
            <img src={logoUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="grid h-full w-full place-items-center">
              {name.charAt(0).toUpperCase()}
            </span>
          )}
          <span className="absolute inset-0 grid place-items-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
            {busy ? <Spinner size="sm" /> : <Camera className="h-6 w-6" />}
          </span>
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onPick(f);
          }}
        />
        <div className="min-w-0 space-y-2">
          <p className="text-ink truncate text-[14px] font-bold">{name}</p>
          <p className="text-ink-subtle text-[12px] font-medium">
            Square JPG, PNG or WebP, up to 2 MB.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => fileInput.current?.click()} disabled={busy}>
              {busy ? 'Saving…' : logoUrl ? 'Change logo' : 'Upload logo'}
            </Button>
            {logoUrl && (
              <Button size="sm" variant="ghost" onClick={() => save.mutate(null)} disabled={busy}>
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Remove
              </Button>
            )}
          </div>
        </div>
      </div>
    </Panel>
  );
}

// ── Profile: picture + name ─────────────────────────────────────────────────

function ProfileCard({ user }: { user: ReturnType<typeof useAuthStore.getState>['user'] }) {
  const [name, setName] = useState(user?.name ?? '');
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const save = useMutation({
    mutationFn: (body: users.UpdateProfileRequest) => api.users.updateProfile(body),
    onSuccess: (r) => {
      patchSessionUser({ name: r.name, avatarUrl: r.avatarUrl ?? null });
      toast.success('Profile updated');
    },
    onError: (e) => toast.error(errMsg(e, 'Could not update profile')),
  });

  const uploadAvatar = async (file: File): Promise<void> => {
    setUploading(true);
    try {
      const id = await uploadImage(file, 'avatars');
      await save.mutateAsync({ avatarFileId: id as users.UpdateProfileRequest['avatarFileId'] });
    } catch (e) {
      if (!(e instanceof ApiClientError)) {
        toast.error(e instanceof Error ? e.message : 'Upload failed');
      }
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const initial = (user?.name ?? 'S').trim().charAt(0).toUpperCase();
  const dirty = name.trim() !== (user?.name ?? '') && name.trim().length >= 2;

  return (
    <Panel title="Profile" description="Your own name and picture in the seller panel.">
      <div className="flex flex-col items-center px-5 py-6">
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          disabled={uploading}
          className="group relative h-24 w-24 overflow-hidden rounded-2xl bg-red-500 text-3xl font-bold text-white shadow-sm ring-4 ring-white"
          aria-label="Change profile picture"
        >
          {user?.avatarUrl ? (
            <img src={user.avatarUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="grid h-full w-full place-items-center">{initial}</span>
          )}
          <span className="absolute inset-0 grid place-items-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
            {uploading ? <Spinner size="sm" /> : <Camera className="h-6 w-6" />}
          </span>
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void uploadAvatar(f);
          }}
        />
        <p className="text-ink-subtle mt-2 text-[12px] font-medium">JPG or PNG, up to 2 MB</p>

        <div className="mt-6 w-full space-y-3">
          <Input
            label="Display name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
          />
          <div className="text-ink-subtle grid gap-1 text-[12.5px] font-medium">
            <span className="flex items-center gap-2">
              <Mail className="h-3.5 w-3.5" /> {user?.email}
            </span>
            <span className="flex items-center gap-2">
              <Phone className="h-3.5 w-3.5" /> {user?.phone}
            </span>
          </div>
          <Button
            onClick={() => save.mutate({ name: name.trim() })}
            disabled={!dirty || save.isPending}
            className="w-full"
          >
            {save.isPending ? 'Saving…' : 'Save name'}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

// ── Password ───────────────────────────────────────────────────────────────

const RULES: Array<{ label: string; test: (v: string) => boolean }> = [
  { label: '8+ characters', test: (v) => v.length >= 8 },
  { label: 'lowercase', test: (v) => /[a-z]/.test(v) },
  { label: 'uppercase', test: (v) => /[A-Z]/.test(v) },
  { label: 'a digit', test: (v) => /\d/.test(v) },
];

function PasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const change = useMutation({
    mutationFn: () => api.auth.changePassword({ currentPassword: current, newPassword: next }),
    onSuccess: (r) => {
      toast.success(
        r.otherSessionsRevoked > 0
          ? `Password changed. ${r.otherSessionsRevoked} other device${r.otherSessionsRevoked === 1 ? '' : 's'} signed out.`
          : 'Password changed.',
      );
      setCurrent('');
      setNext('');
      setConfirm('');
    },
    onError: (e) => toast.error(errMsg(e, 'Could not change password')),
  });

  const unmet = RULES.filter((r) => !r.test(next));
  const ready = current && next && next === confirm && unmet.length === 0 && next !== current;

  return (
    <Panel
      title="Password"
      description="Changing it signs out every other device. This one stays signed in."
      right={<KeyRound className="text-ink-subtle h-4 w-4" />}
    >
      <form
        className="grid gap-3 px-5 py-5 sm:grid-cols-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) change.mutate();
        }}
      >
        <Input
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
        <Input
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={confirm && confirm !== next ? 'Does not match' : undefined}
        />
        <div className="flex flex-wrap items-center justify-between gap-3 sm:col-span-3">
          <div className="flex flex-wrap gap-1.5">
            {RULES.map((r) => {
              const ok = r.test(next);
              return (
                <span
                  key={r.label}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${
                    ok ? 'bg-emerald-50 text-emerald-700' : 'bg-surface-subtle text-ink-subtle'
                  }`}
                >
                  {ok && <Check className="h-3 w-3" />}
                  {r.label}
                </span>
              );
            })}
          </div>
          <Button type="submit" disabled={!ready || change.isPending}>
            {change.isPending ? 'Changing…' : 'Change password'}
          </Button>
        </div>
      </form>
    </Panel>
  );
}

// ── Phone / email, two-step ────────────────────────────────────────────────

function ContactCard({
  channel,
  current,
  verified,
}: {
  channel: 'SMS' | 'EMAIL';
  current: string;
  verified: boolean;
}) {
  const isEmail = channel === 'EMAIL';
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [value, setValue] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<users.StartContactChangeResponse | null>(null);

  const reset = () => {
    setOpen(false);
    setPassword('');
    setValue('');
    setCode('');
    setChallenge(null);
  };

  const start = useMutation({
    mutationFn: () =>
      api.auth.startContactChange({ currentPassword: password, channel, value: value.trim() }),
    onSuccess: (r) => setChallenge(r),
    onError: (e) => toast.error(errMsg(e, 'Could not send the code')),
  });

  const verify = useMutation({
    mutationFn: () =>
      api.auth.verifyContactChange({ challengeToken: challenge!.challengeToken, code }),
    onSuccess: (r) => {
      patchSessionUser(
        isEmail ? { email: r.email, emailVerified: true } : { phone: r.phone, phoneVerified: true },
      );
      toast.success(`${isEmail ? 'Email' : 'Mobile number'} updated and verified.`);
      reset();
    },
    onError: (e) => toast.error(errMsg(e, 'Incorrect code')),
  });

  const label = isEmail ? 'Email address' : 'Mobile number';
  const Icon = isEmail ? Mail : Phone;

  return (
    <Panel
      title={label}
      description={
        isEmail
          ? 'Where order notices and password-reset codes are sent.'
          : 'Where your sign-in code is sent. A wrong number here locks you out, so it is verified before it is saved.'
      }
      right={
        <div className="flex items-center gap-2">
          <span className="text-ink hidden text-[13px] font-semibold sm:inline">{current}</span>
          <Badge tone={verified ? 'success' : 'warning'}>
            {verified ? 'Verified' : 'Unverified'}
          </Badge>
        </div>
      }
    >
      <div className="px-5 py-4">
        {!open ? (
          <Button variant="secondary" onClick={() => setOpen(true)}>
            <Icon className="mr-1.5 h-4 w-4" /> Change {isEmail ? 'email' : 'number'}
          </Button>
        ) : !challenge ? (
          <form
            className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]"
            onSubmit={(e) => {
              e.preventDefault();
              if (password && value.trim()) start.mutate();
            }}
          >
            <Input
              label={`New ${isEmail ? 'email' : 'mobile number'}`}
              type={isEmail ? 'email' : 'tel'}
              inputMode={isEmail ? 'email' : 'tel'}
              placeholder={isEmail ? 'you@example.com' : '+91 98765 43210'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
            />
            <Input
              label="Current password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <div className="flex items-end gap-2">
              <Button type="submit" disabled={!password || !value.trim() || start.isPending}>
                {start.isPending ? 'Sending…' : 'Send code'}
              </Button>
              <Button variant="ghost" type="button" onClick={reset}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <form
            className="grid gap-3 sm:grid-cols-[1fr_auto]"
            onSubmit={(e) => {
              e.preventDefault();
              if (code.length === 6) verify.mutate();
            }}
          >
            <div>
              <p className="text-ink-subtle mb-2 text-[12.5px] font-medium">
                We sent a 6-digit code to{' '}
                <span className="text-ink font-bold">{challenge.sentTo}</span>. Nothing changes
                until you enter it.
              </p>
              <Input
                label="6-digit code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                autoFocus
              />
            </div>
            <div className="flex items-end gap-2">
              <Button type="submit" disabled={code.length !== 6 || verify.isPending}>
                {verify.isPending ? 'Verifying…' : 'Verify & save'}
              </Button>
              <Button variant="ghost" type="button" onClick={reset}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>
    </Panel>
  );
}

// ── Sessions ───────────────────────────────────────────────────────────────

function describeAgent(ua: string | null): string {
  if (!ua) return 'Unknown device';
  const browser = ua.includes('Edg/')
    ? 'Edge'
    : ua.includes('Chrome/')
      ? 'Chrome'
      : ua.includes('Firefox/')
        ? 'Firefox'
        : ua.includes('Safari/')
          ? 'Safari'
          : ua.includes('okhttp') || ua.includes('Expo')
            ? 'Terra app'
            : 'Browser';
  const os = ua.includes('Windows')
    ? 'Windows'
    : ua.includes('Android')
      ? 'Android'
      : /iPhone|iPad/.test(ua)
        ? 'iOS'
        : ua.includes('Mac OS')
          ? 'macOS'
          : ua.includes('Linux')
            ? 'Linux'
            : '';
  return os ? `${browser} on ${os}` : browser;
}

function SessionsCard() {
  const qc = useQueryClient();
  const [password, setPassword] = useState('');
  const [confirming, setConfirming] = useState(false);

  const sessions = useQuery({
    queryKey: ['my-sessions'],
    queryFn: () => api.auth.listSessions(),
  });

  const revoke = useMutation({
    mutationFn: () => api.auth.revokeOtherSessions(password),
    onSuccess: (r) => {
      toast.success(`Signed out of ${r.revoked} other device${r.revoked === 1 ? '' : 's'}.`);
      setPassword('');
      setConfirming(false);
      void qc.invalidateQueries({ queryKey: ['my-sessions'] });
    },
    onError: (e) => toast.error(errMsg(e, 'Could not sign out other devices')),
  });

  const others = (sessions.data ?? []).filter((s) => !s.current).length;

  return (
    <Panel
      title="Signed-in devices"
      description="Everywhere this account is currently signed in."
      right={<ShieldCheck className="text-ink-subtle h-4 w-4" />}
    >
      {sessions.isLoading ? (
        <div className="grid min-h-[100px] place-items-center">
          <Spinner size="md" />
        </div>
      ) : (
        <ul className="divide-border/40 divide-y">
          {(sessions.data ?? []).map((s) => (
            <li key={s.id} className="flex items-center gap-3 px-5 py-3">
              <Monitor className="text-ink-subtle h-4 w-4 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-ink truncate text-[13px] font-semibold">
                  {describeAgent(s.userAgent)}
                  {s.current && (
                    <Badge tone="success" className="ml-2">
                      This device
                    </Badge>
                  )}
                </p>
                <p className="text-ink-subtle text-[11.5px] font-medium">
                  {s.ip ?? 'IP unknown'} · since{' '}
                  {new Date(s.createdAt).toLocaleString('en-IN', {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
      {others > 0 && (
        <div className="border-border/60 border-t px-5 py-4">
          {!confirming ? (
            <Button variant="secondary" onClick={() => setConfirming(true)}>
              <LogOut className="mr-1.5 h-4 w-4" /> Sign out {others} other device
              {others === 1 ? '' : 's'}
            </Button>
          ) : (
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (password) revoke.mutate();
              }}
            >
              <Input
                label="Confirm with your password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
              />
              <Button type="submit" disabled={!password || revoke.isPending}>
                {revoke.isPending ? 'Signing out…' : 'Sign out others'}
              </Button>
              <Button variant="ghost" type="button" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
            </form>
          )}
        </div>
      )}
    </Panel>
  );
}
